import React, { useEffect, useState } from "react";
import { Save, UserPlus, Download, Upload } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { useApp } from "@/context/AppContext";

const ROLES = ["super_admin", "competition_admin", "moderator", "analytics_admin", "content_admin"];

function Section({ title, desc, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <h2 className="font-heading font-bold text-slate-900">{title}</h2>
      {desc && <p className="text-xs text-slate-400 mt-1 mb-4">{desc}</p>}
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">{label}</label><div className="mt-1.5">{children}</div></div>;
}

export default function AdminSettings() {
  const { admin, loadConfig } = useApp();
  const [s, setS] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ email: "", name: "", password: "", role: "moderator" });

  const load = async () => {
    try {
      const [{ data: st }, { data: ad }] = await Promise.all([api.get("/admin/settings"), api.get("/admin/admins")]);
      setS(st);
      setAdmins(ad.admins);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    try {
      await api.put("/admin/settings", patch);
      toast.success("Settings saved");
      loadConfig();
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addAdmin = async () => {
    try {
      await api.post("/admin/admins", newAdmin);
      toast.success("Admin created");
      setNewAdmin({ email: "", name: "", password: "", role: "moderator" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadMusic = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/media/upload", fd);
      set("media.music_url", data.url);
      toast.success("Audio uploaded — click Save Media to apply");
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!s) return <div className="text-slate-400 font-semibold py-20 text-center">Loading settings…</div>;
  const set = (path, value) => {
    const next = { ...s };
    const keys = path.split(".");
    let obj = next;
    for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
    obj[keys[keys.length - 1]] = value;
    setS(next);
  };

  return (
    <div data-testid="admin-settings" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tighter">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Everything configurable without touching code.</p>
      </div>

      <Section title="Branding & Competition" desc="Logo URLs are used across the site, loading screen, emails and Discord embeds.">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Competition Title"><Input data-testid="set-title" value={s.competition.title} onChange={(e) => set("competition.title", e.target.value)} /></Field>
          <Field label="Organization"><Input data-testid="set-org" value={s.competition.org} onChange={(e) => set("competition.org", e.target.value)} /></Field>
          <Field label="Tagline"><Input data-testid="set-tagline" value={s.competition.tagline} onChange={(e) => set("competition.tagline", e.target.value)} /></Field>
          <Field label="Support Email"><Input data-testid="set-support" value={s.support_email} onChange={(e) => set("support_email", e.target.value)} /></Field>
          <Field label="Logo URL"><Input data-testid="set-logo" value={s.branding.logo_url} onChange={(e) => set("branding.logo_url", e.target.value)} placeholder="https://…" /></Field>
          <Field label="Loading Screen Logo URL"><Input data-testid="set-loading-logo" value={s.branding.loading_logo_url} onChange={(e) => set("branding.loading_logo_url", e.target.value)} placeholder="https://…" /></Field>
        </div>
        <button onClick={() => save({ competition: s.competition, branding: s.branding, support_email: s.support_email })} data-testid="save-branding" className="px-6 py-2.5 rounded-lg bg-[#003366] text-white text-sm font-bold inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
      </Section>

      <Section title="Scoring & Gameplay" desc="Applied server-side for every answer.">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Base Points"><Input data-testid="set-base-points" type="number" value={s.scoring.base_points} onChange={(e) => set("scoring.base_points", Number(e.target.value))} /></Field>
          <Field label="Max Speed Bonus"><Input data-testid="set-speed-bonus" type="number" value={s.scoring.max_speed_bonus} onChange={(e) => set("scoring.max_speed_bonus", Number(e.target.value))} /></Field>
          <Field label="Question Timer (s)"><Input data-testid="set-qtimer" type="number" value={s.scoring.question_timer} onChange={(e) => set("scoring.question_timer", Number(e.target.value))} /></Field>
          <Field label="Wrong Penalty"><Input data-testid="set-wrong-penalty" type="number" value={s.scoring.wrong_penalty} onChange={(e) => set("scoring.wrong_penalty", Number(e.target.value))} /></Field>
          <Field label="Unanswered Penalty"><Input data-testid="set-unanswered-penalty" type="number" value={s.scoring.unanswered_penalty} onChange={(e) => set("scoring.unanswered_penalty", Number(e.target.value))} /></Field>
          <Field label="Category Multiplier"><Input data-testid="set-multiplier" type="number" step="0.1" value={s.scoring.category_multiplier} onChange={(e) => set("scoring.category_multiplier", Number(e.target.value))} /></Field>
        </div>
        <div className="flex flex-wrap gap-8 pt-2">
          <label className="flex items-center gap-3 text-sm font-semibold text-slate-600"><Switch data-testid="set-auto-submit" checked={s.scoring.auto_submit} onCheckedChange={(v) => set("scoring.auto_submit", v)} /> Auto-submit on selection</label>
          <label className="flex items-center gap-3 text-sm font-semibold text-slate-600"><Switch data-testid="set-randomize-q" checked={s.scoring.randomize_questions} onCheckedChange={(v) => set("scoring.randomize_questions", v)} /> Randomize question order</label>
          <label className="flex items-center gap-3 text-sm font-semibold text-slate-600"><Switch data-testid="set-randomize-o" checked={s.scoring.randomize_options} onCheckedChange={(v) => set("scoring.randomize_options", v)} /> Randomize answer order</label>
        </div>
        <button onClick={() => save({ scoring: s.scoring })} data-testid="save-scoring" className="px-6 py-2.5 rounded-lg bg-[#003366] text-white text-sm font-bold inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save Scoring</button>
      </Section>

      <Section title="Google & Test Login" desc="REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH. Add your Google OAuth Client ID here; whitelist your site origin in Google Cloud Console.">
        <Field label="Google OAuth Client ID"><Input data-testid="set-google-client" value={s.google.client_id} onChange={(e) => set("google.client_id", e.target.value)} placeholder="xxxx.apps.googleusercontent.com" /></Field>
        <label className="flex items-center gap-3 text-sm font-semibold text-slate-600"><Switch data-testid="set-test-login" checked={s.test_login_enabled} onCheckedChange={(v) => set("test_login_enabled", v)} /> Enable test login (for demos/testing)</label>
        <button onClick={() => save({ google: s.google, test_login_enabled: s.test_login_enabled })} data-testid="save-auth" className="px-6 py-2.5 rounded-lg bg-[#003366] text-white text-sm font-bold inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save Auth</button>
      </Section>

      <Section title="Social Links">
        <div className="grid md:grid-cols-2 gap-4">
          {["instagram", "discord", "youtube", "website", "minecraft", "twitter"].map((k) => (
            <Field key={k} label={k[0].toUpperCase() + k.slice(1)}><Input data-testid={`set-social-${k}`} value={s.socials[k] || ""} onChange={(e) => set(`socials.${k}`, e.target.value)} /></Field>
          ))}
        </div>
        <button onClick={() => save({ socials: s.socials })} data-testid="save-socials" className="px-6 py-2.5 rounded-lg bg-[#003366] text-white text-sm font-bold inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save Socials</button>
      </Section>

      <Section title="Home Music & Sounds" desc="Paste an audio URL (mp3/ogg/m4a) — it loops on the home screen. Browsers start playback after the visitor's first tap; a floating play/pause button is shown. Sounds = quiz interaction sound effects.">
        <Field label="Music URL (paste a link or upload a file)">
          <div className="flex gap-2">
            <Input data-testid="set-music-url" value={s.media?.music_url || ""} onChange={(e) => set("media.music_url", e.target.value)} placeholder="https://example.com/anthem.mp3" />
            <label data-testid="upload-music-btn" className="px-4 py-2 rounded-lg border-2 border-[#138808] text-[#138808] text-sm font-bold hover:bg-[#138808] hover:text-white transition-colors inline-flex items-center gap-2 cursor-pointer shrink-0">
              <Upload className="w-4 h-4" /> Upload
              <input type="file" accept="audio/*,.mp3,.ogg,.wav,.m4a" className="hidden" onChange={uploadMusic} data-testid="upload-music-input" />
            </label>
          </div>
        </Field>
        <label className="flex items-center gap-3 text-sm font-semibold text-slate-600"><Switch data-testid="set-music-enabled" checked={!!s.media?.music_enabled} onCheckedChange={(v) => set("media.music_enabled", v)} /> Enable home screen music</label>
        <label className="flex items-center gap-3 text-sm font-semibold text-slate-600"><Switch data-testid="set-sounds-enabled" checked={s.media?.sounds_enabled ?? true} onCheckedChange={(v) => set("media.sounds_enabled", v)} /> Enable quiz interaction sounds (default for everyone)</label>
        <button onClick={() => save({ media: s.media })} data-testid="save-media" className="px-6 py-2.5 rounded-lg bg-[#003366] text-white text-sm font-bold inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save Media</button>
      </Section>

      <Section title="Anti-Cheat Policy" desc="What happens when a participant exceeds the tab-switch threshold.">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Action">
            <Select value={s.anticheat.action} onValueChange={(v) => set("anticheat.action", v)}>
              <SelectTrigger data-testid="set-ac-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="warning">Warning only</SelectItem>
                <SelectItem value="penalty">Score penalty</SelectItem>
                <SelectItem value="freeze">Freeze quiz (manual review)</SelectItem>
                <SelectItem value="disqualify">Flag for disqualification</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tab-switch Threshold"><Input data-testid="set-ac-threshold" type="number" value={s.anticheat.tab_threshold} onChange={(e) => set("anticheat.tab_threshold", Number(e.target.value))} /></Field>
        </div>
        <button onClick={() => save({ anticheat: s.anticheat })} data-testid="save-anticheat" className="px-6 py-2.5 rounded-lg bg-[#003366] text-white text-sm font-bold inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save Policy</button>
      </Section>

      <Section title="Custom Rules (HTML)" desc="Overrides the built-in rules page content.">
        <Textarea data-testid="set-rules" rows={6} className="font-mono text-xs" value={s.rules_html} onChange={(e) => set("rules_html", e.target.value)} placeholder="<h2>My custom rules…</h2>" />
        <button onClick={() => save({ rules_html: s.rules_html })} data-testid="save-rules" className="px-6 py-2.5 rounded-lg bg-[#003366] text-white text-sm font-bold inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save Rules</button>
      </Section>

      {admin?.role === "super_admin" && (
        <Section title="Admin Accounts" desc="Role-based access: super_admin, competition_admin, moderator, analytics_admin, content_admin.">
          <div className="space-y-2">
            {admins.map((a) => (
              <div key={a.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2.5 text-sm">
                <span className="font-semibold text-slate-700">{a.name} <span className="text-slate-400 font-normal">({a.email})</span></span>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#003366]/10 text-[#003366]">{a.role?.replace("_", " ")}</span>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-5 gap-3 pt-3">
            <Input data-testid="new-admin-email" placeholder="email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} />
            <Input data-testid="new-admin-name" placeholder="name" value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} />
            <Input data-testid="new-admin-password" placeholder="password" type="password" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} />
            <Select value={newAdmin.role} onValueChange={(v) => setNewAdmin({ ...newAdmin, role: v })}>
              <SelectTrigger data-testid="new-admin-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={addAdmin} data-testid="add-admin-btn" className="py-2 rounded-lg bg-[#FF9933] text-white text-sm font-bold inline-flex items-center justify-center gap-2"><UserPlus className="w-4 h-4" /> Add</button>
          </div>
        </Section>
      )}

      <Section title="Backup & Exports">
        <div className="flex flex-wrap gap-3">
          <a href={`${API}/admin/backup`} data-testid="backup-download" className="px-5 py-2.5 rounded-lg bg-[#003366] text-white text-sm font-bold inline-flex items-center gap-2"><Download className="w-4 h-4" /> Full Backup (JSON)</a>
          <a href={`${API}/admin/export/leaderboard`} data-testid="export-leaderboard" className="px-5 py-2.5 rounded-lg border border-slate-300 text-sm font-bold text-slate-600 inline-flex items-center gap-2"><Download className="w-4 h-4" /> Leaderboard CSV</a>
          <a href={`${API}/admin/export/anticheat`} data-testid="export-anticheat" className="px-5 py-2.5 rounded-lg border border-slate-300 text-sm font-bold text-slate-600 inline-flex items-center gap-2"><Download className="w-4 h-4" /> Anti-Cheat CSV</a>
        </div>
      </Section>
    </div>
  );
}
